import { useCallback, useEffect, useMemo, useState } from 'react';

export const TABLE_PAGE_SIZE = 20;

export function sortRows(rows, sortKey, sortDir, accessors = {}) {
  if (!sortKey || !accessors[sortKey]) return rows || [];
  const acc = accessors[sortKey];
  const dir = sortDir === 'desc' ? -1 : 1;
  return [...(rows || [])].sort((a, b) => {
    const av = acc(a);
    const bv = acc(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
  });
}

export function buildOrderParam(sortKey, sortDir, fieldMap = {}) {
  const field = fieldMap[sortKey];
  if (!field) return null;
  return `${field}.${sortDir === 'desc' ? 'desc' : 'asc'}`;
}

/** Client-side filter + sort + page for in-memory tables. */
export function useTableTools(rows, {
  pageSize = TABLE_PAGE_SIZE,
  defaultSort = { key: null, dir: 'asc' },
  sortKey: controlledKey,
  sortDir: controlledDir,
  onSort: controlledOnSort,
  accessors = {},
  search = '',
  searchFn = null,
  apiPaged = false,
} = {}) {
  const [sortKey, setSortKey] = useState(defaultSort.key);
  const [sortDir, setSortDir] = useState(defaultSort.dir || 'asc');
  const [shown, setShown] = useState(pageSize);

  const sk = controlledKey ?? sortKey;
  const sd = controlledDir ?? sortDir;

  const toggleSort = useCallback((key) => {
    if (controlledOnSort) { controlledOnSort(key); return; }
    if (!key) return;
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return key;
    });
    setShown(pageSize);
  }, [controlledOnSort, pageSize]);

  const filtered = useMemo(() => {
    const list = rows || [];
    if (!search) return list;
    if (searchFn) return list.filter((r) => searchFn(r, search));
    return list;
  }, [rows, search, searchFn]);

  const sorted = useMemo(
    () => (apiPaged ? filtered : sortRows(filtered, sk, sd, accessors)),
    [filtered, sk, sd, accessors, apiPaged],
  );

  useEffect(() => { setShown(pageSize); }, [rows?.length, search, sk, sd, pageSize]);

  const visible = apiPaged ? sorted : sorted.slice(0, shown);
  const hasMore = !apiPaged && shown < sorted.length;
  const loadMore = useCallback(() => setShown((s) => s + pageSize), [pageSize]);

  return {
    visible,
    sorted,
    total: sorted.length,
    sortKey: sk,
    sortDir: sd,
    toggleSort,
    hasMore,
    loadMore,
    shownCount: visible.length,
  };
}
